import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { login } from '../store/slices/authSlice';
import { forgotPassword, loginUser, resetPassword } from '../services/api';
import { APP_VERSION } from '../config/buildInfo';
import { useBrand } from '../context/BrandContext';

const Login = () => {
  const [mode, setMode] = useState('sign-in');
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { brand, assets } = useBrand();

  const handleInputChange = (e) => {
    setErrorMessage('');
    setSuccessMessage('');
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      if (mode === 'forgot') {
        const { data } = await forgotPassword({ email: formData.email });
        setSuccessMessage(data.message || 'If an account exists for that email, a password reset link has been sent.');
        return;
      }

      const response = await loginUser(formData);
      const { data } = response;
      dispatch(login(data));
      window.history.replaceState(null, '', '/dashboard');
      navigate('/dashboard', { replace: true });
    } catch (error) {
      setErrorMessage(error.response?.data?.error || 'Invalid login credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <section className="login-visual" style={{ backgroundImage: `url('${assets.heroUrl}')` }}>
        <div className="login-brand">
          <img src={assets.logoUrl} alt={brand.productName} />
          <span>v{APP_VERSION}</span>
        </div>
        <div className="login-visual-copy">
          <h1>{brand.productName}</h1>
          <p>Continue to manage occurrence variance reports, action plans, and registry records.</p>
          <div className="login-visual-points">
            <span><i className="bi bi-shield-check" aria-hidden="true"></i> Secure workflow</span>
            <span><i className="bi bi-clipboard2-check" aria-hidden="true"></i> {brand.taglineShort || 'OVR registry'}</span>
          </div>
        </div>
      </section>

      <section className="login-panel-wrap">
        <div className="login-panel">
          <div className="login-panel-header">
            <img src={assets.logoUrl} alt={brand.productName} />
            <div>
              <h2>{mode === 'forgot' ? 'Forgot password' : 'Sign in'}</h2>
              <p>
                {mode === 'forgot'
                  ? 'Enter your account email. We will send a reset link.'
                  : 'Use your registered account to continue.'}
              </p>
            </div>
          </div>

          {errorMessage && (
            <div className="login-error">
              <i className="bi bi-exclamation-circle" aria-hidden="true"></i>
              {errorMessage}
            </div>
          )}

          {successMessage && (
            <div className="login-success">
              <i className="bi bi-check-circle" aria-hidden="true"></i>
              {successMessage}
            </div>
          )}

        <form onSubmit={handleSubmit}>
          <label className="login-field">
            <span>Email</span>
            <div className="login-input-wrap">
              <i className="bi bi-envelope" aria-hidden="true"></i>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                autoComplete="username"
              />
            </div>
          </label>

          {mode === 'sign-in' && (
            <label className="login-field">
              <span>Password</span>
              <div className="login-input-wrap">
                <i className="bi bi-lock" aria-hidden="true"></i>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} aria-hidden="true"></i>
                </button>
              </div>
            </label>
          )}

          {mode === 'sign-in' && (
            <div className="login-forgot">
              <button
                type="button"
                onClick={() => {
                  setMode('forgot');
                  setErrorMessage('');
                  setSuccessMessage('');
                }}
              >
                Forgot password?
              </button>
            </div>
          )}

          <button type="submit" className="login-submit-btn" disabled={loading}>
            {loading
              ? (mode === 'forgot' ? 'Sending...' : 'Signing in...')
              : (mode === 'forgot' ? 'Send reset link' : 'Sign In')}
          </button>

          {mode === 'forgot' && (
            <button
              type="button"
              className="login-back-link"
              onClick={() => {
                setMode('sign-in');
                setErrorMessage('');
                setSuccessMessage('');
              }}
            >
              Back to sign in
            </button>
          )}
        </form>
        </div>
      </section>
    </div>
  );
};

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const { brand, assets } = useBrand();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState(token ? '' : 'This reset link is missing a token.');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (!token) {
      setErrorMessage('This reset link is missing a token.');
      return;
    }

    if (password.length < 6) {
      setErrorMessage('New password must be at least 6 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const { data } = await resetPassword({ token, password });
      setSuccessMessage(data.message || 'Password updated. You can sign in now.');
      setTimeout(() => navigate('/login', { replace: true }), 1200);
    } catch (error) {
      setErrorMessage(error.response?.data?.error || 'Could not reset the password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <section className="login-visual" style={{ backgroundImage: `url('${assets.heroUrl}')` }}>
        <div className="login-brand">
          <img src={assets.logoUrl} alt={brand.productName} />
          <span>v{APP_VERSION}</span>
        </div>
        <div className="login-visual-copy">
          <h1>{brand.productName}</h1>
          <p>Choose a new password for your account.</p>
        </div>
      </section>

      <section className="login-panel-wrap">
        <div className="login-panel">
          <div className="login-panel-header">
            <img src={assets.logoUrl} alt={brand.productName} />
            <div>
              <h2>Reset password</h2>
              <p>Enter a new password for your account.</p>
            </div>
          </div>

          {errorMessage && (
            <div className="login-error">
              <i className="bi bi-exclamation-circle" aria-hidden="true"></i>
              {errorMessage}
            </div>
          )}

          {successMessage && (
            <div className="login-success">
              <i className="bi bi-check-circle" aria-hidden="true"></i>
              {successMessage}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <label className="login-field">
              <span>New password</span>
              <div className="login-input-wrap">
                <i className="bi bi-lock" aria-hidden="true"></i>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} aria-hidden="true"></i>
                </button>
              </div>
            </label>

            <label className="login-field">
              <span>Confirm password</span>
              <div className="login-input-wrap">
                <i className="bi bi-lock" aria-hidden="true"></i>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
              </div>
            </label>

            <button type="submit" className="login-submit-btn" disabled={loading || !token}>
              {loading ? 'Saving...' : 'Update password'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Login;
